import { memo, useCallback, useMemo, useState } from "react";
import {
    approveAccrualReviewRequest,
    useApproveWithScheduleResolutions,
} from "@components/accounting/schedules/linkedConflict/useApproveWithScheduleResolutions";
import { hasDocumentsLeftAfterUnmatch } from "@components/accounting/transactionMatch/schedule/matchResolutions";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { TransactionColumn } from "@common/types/views/transactionViews";
import { getDefaultAccrualAccountId } from "@common/helpers/accrualAccount";
import {
    getLineItemAccountIdFromSuggestions,
    getTransferSuggestion,
} from "@common/schemas/domains/accrualReview/accrual-match-suggestion-display.schema";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useAccounts } from "@hooks/useAccounts";
import { useEntityIdForGLAccount } from "@hooks/useEntityIdForGLAccount";
import { useHasAccrualLedger, useLedgers } from "@api/ledger.api";
import { findPrimaryLedgerId } from "@lib/accountOverrides";
import type { AccountOverrideEntryRequest } from "@common/schemas/domains/accounting/account-override.schema";
import { CheckIcon, RevertIcon } from "@icons";
import { useScope } from "@state/scopeContext.lib";
import { undoAccrualReview } from "@api/accrual-review.api";
import { useOverlays } from "contexts/OverlayContext";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useIsTableReadonly } from "@hooks/useIsReadonlyOrLocked";
import { suggestionToMatchableItem } from "@components/accounting/transactionMatch/mappers";
import { MatchableItem } from "@components/accounting/transactionMatch/types";
import {
    isTransactionReviewRequired,
    buildApproveBody,
    getAccrualReviewActionDecision,
} from "./accrualReviewBulkApprove";
import { DEFAULT_ACCRUAL_REVIEW_LEDGER_KEY } from "./accrualReview.constants";
import {
    applyApproveToAccrualReviewCache,
    applyUndoToAccrualReviewCache,
    removeRevertedFromReviewedAccrualReviewCache,
} from "./accrualReviewCache";
import { useAccrualReviewContext } from "./useAccrualReviewContext";
import { AccrualConfirmButton } from "./AccrualConfirmButton";
import {
    getAccrualConfirmChangeCount,
    getAccrualConfirmDisplay,
} from "./accrualConfirmChangeCount";
import { useCellActionOpener } from "./useCellActionOpener";
import styles from "./AccrualReviewPage.module.scss";

export const AccrualActionCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, TransactionListItem, unknown>) => {
        const transaction = row.original;
        const isTableReadonly = useIsTableReadonly(table);
        const { overrides, isMatchDismissed, confirmDisplay } =
            useAccrualReviewContext();
        const { workspaceId } = useScope();
        const { openOverlay } = useOverlays();
        const { refuseIfViewOnly } = useViewOnlyRefusal();

        const entityIdForGLAccount = useEntityIdForGLAccount(transaction);
        const hasAccrualLedger = useHasAccrualLedger(entityIdForGLAccount);
        const { data: ledgers, isLoading: isLoadingLedgers } =
            useLedgers(entityIdForGLAccount);
        const primaryAccrualLedgerId = useMemo(
            () => findPrimaryLedgerId(ledgers, AccountingLedgerBasis.accruals),
            [ledgers],
        );
        const accrualLedgers = useMemo(
            () =>
                (ledgers ?? []).filter(
                    (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
                ),
            [ledgers],
        );
        const { accounts, isLoading: isLoadingAccounts } = useAccounts({
            entityId: hasAccrualLedger ? entityIdForGLAccount : undefined,
            ledgerBasis: AccountingLedgerBasis.accruals,
        });
        const isMatchDismissedForTx = isMatchDismissed(transaction.id);

        const activeSuggestions = useMemo(
            () =>
                isMatchDismissedForTx
                    ? []
                    : (transaction.accrualMatchSuggestions ?? []),
            [transaction.accrualMatchSuggestions, isMatchDismissedForTx],
        );

        const lineItemAccountId =
            getLineItemAccountIdFromSuggestions(activeSuggestions);

        const hasDocumentSuggestion = activeSuggestions.some(
            (s) => s.invoice ?? s.bill,
        );

        // Mirrors the suggestion-cell render rule (`AccrualMatchCell`): the
        // pill renders when there's a doc suggestion, or as a transfer
        // fallback when no doc suggestions are present. Drives whether the
        // primary action button labels itself "Match" or falls back to
        // "Confirm".
        const hasActiveMatchSuggestion = useMemo(() => {
            if (hasDocumentSuggestion) {
                return true;
            }
            return !!getTransferSuggestion(activeSuggestions)
                ?.matchedTransaction;
        }, [hasDocumentSuggestion, activeSuggestions]);

        const defaultAccountId = getDefaultAccrualAccountId({
            transaction,
            accounts,
            lineItemAccountId,
            ignoreMatches: !hasDocumentSuggestion,
            hasDocumentMatch: hasDocumentSuggestion,
        });

        const [isLoading, setIsLoading] = useState(false);

        const isAccepted = !!transaction.accrualAccountId;

        const needsReview = useMemo(
            () => isTransactionReviewRequired(transaction, activeSuggestions),
            [transaction, activeSuggestions],
        );

        // Resolve outside the memo so the dependency is the resolved id
        // (a primitive), not the `getProposedAccountId` function — its
        // identity changes whenever the overrides map is replaced, which
        // would needlessly recompute the memo.
        const proposedAccountId =
            transaction.accrualAccountId ??
            overrides
                .get(transaction.id)
                ?.get(DEFAULT_ACCRUAL_REVIEW_LEDGER_KEY) ??
            null;
        const proposedAccountIdsByLedgerName = useMemo(
            () =>
                new Map(
                    accrualLedgers.map((ledger) => [
                        ledger.name,
                        overrides.get(transaction.id)?.get(ledger.name) ??
                            proposedAccountId,
                    ]),
                ),
            [accrualLedgers, overrides, proposedAccountId, transaction.id],
        );
        const accountOverrides = useMemo<AccountOverrideEntryRequest[]>(
            () =>
                accrualLedgers.flatMap((ledger) => {
                    const accountId =
                        proposedAccountIdsByLedgerName.get(ledger.name) ??
                        proposedAccountId ??
                        defaultAccountId;
                    return accountId
                        ? [{ accountingLedgerId: ledger.id, accountId }]
                        : [];
                }),
            [
                accrualLedgers,
                defaultAccountId,
                proposedAccountId,
                proposedAccountIdsByLedgerName,
            ],
        );

        const fallbackAccountId = proposedAccountId ?? defaultAccountId;
        const changeCount = getAccrualConfirmChangeCount({
            accrualLedgerCount: accrualLedgers.length,
            accountOverrideCount: accountOverrides.length,
            hasFallbackAccount: !!fallbackAccountId,
            hasActiveMatchSuggestion,
        });

        // A rule-suggested row with nothing to match against confirms straight
        // to the rule's account, so "Confirm" needs that account to be valid.
        // If it is missing from the entity's accrual COA (e.g. deleted,
        // disabled, or belonging to a different entity) fall back to "Review"
        // so the user can pick a replacement instead of clicking a no-op
        // button.
        const isRuleConfirmOnly =
            !hasActiveMatchSuggestion &&
            !!transaction.suggestedAccrualAccountId &&
            !transaction.accrualAccountId;

        const ruleAccountIsActionable = useMemo(() => {
            if (!isRuleConfirmOnly) {
                return true;
            }
            const candidateId =
                proposedAccountId ??
                transaction.suggestedAccrualAccountId ??
                defaultAccountId;
            if (!candidateId) {
                return false;
            }
            return accounts.some((a) => a.id === candidateId && !a.isDisabled);
        }, [
            isRuleConfirmOnly,
            proposedAccountId,
            transaction.suggestedAccrualAccountId,
            defaultAccountId,
            accounts,
        ]);

        const { approveWithScheduleResolutions, scheduleResolutionModals } =
            useApproveWithScheduleResolutions();

        const handleAccept = useCallback(async () => {
            if (!workspaceId) {
                return;
            }

            const body = buildApproveBody({
                transaction,
                suggestions: activeSuggestions,
                proposedAccountId: fallbackAccountId,
                primaryAccrualLedgerId: primaryAccrualLedgerId ?? null,
                accountOverrides,
            });

            if (!body) {
                return;
            }

            setIsLoading(true);
            try {
                await approveWithScheduleResolutions({
                    request: approveAccrualReviewRequest({
                        workspaceId,
                        body,
                    }),
                    hasWorkLeft: (resolutions) =>
                        hasDocumentsLeftAfterUnmatch({ body, resolutions }),
                    onApproved: (response) => {
                        applyApproveToAccrualReviewCache(
                            transaction.id,
                            response,
                        );
                    },
                });
            } catch {
                // toasted by approveWithScheduleResolutions
            } finally {
                setIsLoading(false);
            }
        }, [
            workspaceId,
            transaction,
            fallbackAccountId,
            activeSuggestions,
            primaryAccrualLedgerId,
            accountOverrides,
            approveWithScheduleResolutions,
        ]);

        const handleOpenMatch = useCallback(() => {
            if (refuseIfViewOnly()) {
                return;
            }
            const suggestedItems: MatchableItem[] = activeSuggestions
                .map((s) => suggestionToMatchableItem(s, transaction.entity))
                .filter((item): item is MatchableItem => item != null);

            openOverlay("transactionMatch", {
                transactionId: transaction.id,
                suggestedItems:
                    suggestedItems.length > 0 ? suggestedItems : undefined,
            });
        }, [
            openOverlay,
            transaction.id,
            transaction.entity,
            activeSuggestions,
            refuseIfViewOnly,
        ]);

        const handleRevert = useCallback(async () => {
            if (!workspaceId) {
                return;
            }

            setIsLoading(true);
            try {
                const response = await undoAccrualReview({
                    workspaceId,
                    transactionId: transaction.id,
                });

                applyUndoToAccrualReviewCache(response);
                if (isTableReadonly) {
                    removeRevertedFromReviewedAccrualReviewCache(response);
                }
            } catch {
                // error toast handled by axios interceptor
            } finally {
                setIsLoading(false);
            }
        }, [workspaceId, transaction, isTableReadonly]);

        const actionDecision = getAccrualReviewActionDecision({
            hasActiveMatchSuggestion,
            needsReview,
            ruleAccountIsActionable,
        });
        const { isConfirmShown, badgeCount } = getAccrualConfirmDisplay({
            action: actionDecision.action,
            changeCount,
            confirmDisplay,
        });
        const confirmAction = isLoading ? undefined : handleAccept;
        const primaryAction = isConfirmShown ? confirmAction : handleOpenMatch;

        // The change count needs the row's accrual ledgers and accounts, so
        // the badge and the Confirm / Review choice wait for both.
        const isChangeCountPending =
            confirmDisplay === "withChangeCount" &&
            (isLoadingLedgers || isLoadingAccounts);
        const hasRowAction =
            !isAccepted && !isTableReadonly && !isChangeCountPending;

        useCellActionOpener({
            table,
            columnId: TransactionColumn.ACCRUAL_ACTIONS,
            rowId: transaction.id,
            open: hasRowAction ? primaryAction : undefined,
        });

        if (isAccepted) {
            return (
                <ViewTableCell className={styles.centeredCell}>
                    <div className={styles.acceptedAction}>
                        <span
                            className={classNames(
                                styles.actionIcon,
                                styles.checkAction,
                                "icon-color-gray-900",
                            )}
                        >
                            <CheckIcon />
                        </span>
                        <span className={styles.undoAction}>
                            <Button
                                variant="primary"
                                size="md"
                                onClick={handleRevert}
                                disabled={isLoading}
                                iconOnly
                                aria-label="Revert accrual review"
                            >
                                <RevertIcon />
                            </Button>
                        </span>
                    </div>
                </ViewTableCell>
            );
        }

        if (isTableReadonly || isChangeCountPending) {
            return <ViewTableCell className={styles.centeredCell} />;
        }

        const kebabMenu = (
            <Menu.More size="md" className={viewTableStyles.showOnHover}>
                <Menu.Item onClick={handleOpenMatch}>Find the match</Menu.Item>
            </Menu.More>
        );

        if (!isConfirmShown) {
            return (
                <ViewTableCell className={styles.centeredCell}>
                    <div className={styles.actionButtons}>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={handleOpenMatch}
                            className={viewTableStyles.showOnHover}
                        >
                            Review
                        </Button>
                        {kebabMenu}
                    </div>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell className={styles.centeredCell}>
                {scheduleResolutionModals}
                <div className={styles.actionButtons}>
                    <AccrualConfirmButton
                        label={actionDecision.label}
                        changeCount={badgeCount}
                        onClick={handleAccept}
                        disabled={isLoading}
                    />
                    {kebabMenu}
                </div>
            </ViewTableCell>
        );
    },
);

AccrualActionCell.displayName = "AccrualActionCell";
